import { Image } from "@mantine/core";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import moment from "moment";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useMutation } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import event from "../../services/event";
import { rupiah } from "../../utils/currency";
import Category from "../category/Category";
import BulkEmailResend from "../../components/BulkEmailResend";
import ProtectedPermissionView from "../../components/ProtectedPermissionView";

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
}

const chartSetting = { height: 300 };

export default function EventDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.split("/");
  const event_id = path.pop() || "";
  const [data, setData] = useState({
    id: "",
    name: "",
    description: "",
    is_persen: false,
    fee: 0,
    ppn: 0,
    ticket_sold: 0,
    total: 0,
    total_discount: 0,
    total_fee: 0,
    total_transaction_fee: 0,
    sod_fee: 0,
    gross: 0,
    total_tax: 0,
    created_time: "",
    created_by: "",
    photo: "",
    slug: "",
  });
  const [ticketStats, setTicketStats] = useState([{ grouped_days: "", count: "0" }]);
  const notification = useNotification();

  const downloadEventInvitations = useMutation(
    "downloadEventInvitations",
    () => event.downloadEventInvitations(data.id),
    {
      onSuccess: (response: any) => {
        notification.onOpen({
          message: "Event invitations downloaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.error !== ""
              ? error.response.data.error
              : "Something went wrong",
          type: "error",
          position: "top",
        });
      },
    }
  );

  useEffect(() => {
    event.viewEventDetail(event_id).then((response) => {
      setData(response.data);
      if (response?.data?.ticket_stats?.length > 0) {
        for (const i of response.data.ticket_stats) {
          const date = new Date(i.grouped_days);
          i.grouped_days = `${date.getMonth() + 1}-${date.getDate()}`;
          i.count = Number(i.count);
        }
        setTicketStats(response.data.ticket_stats);
      }
    });
  }, [event_id]);

  const handleNavigateToOrders = () => {
    navigate(`/admin/order/${data.id}`);
  };

  const InfoItem = ({ icon, label, value, color = "green" }: InfoItemProps) => (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
      }}
    >
      {icon}
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body1"
          color={color}
          fontWeight="700"
          sx={{ mt: 0.5 }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <MainLayout>
      <Helmet>
        <title>{data.name || 'Event Detail'}</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[{ label: "Event", href: "/admin/event" }]}
        label={data.name}
        href={"/admin/event"}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 3]}>
        <Box sx={{ mt: 3, mb: 4 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(to right, #f8f9fa, #ffffff)'
            }}
          >
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {data.name}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => event.downloadReport({ id: data.id, name: data.name })}
              >
                Download Report
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => event.downloadSales({ id: data.id, name: data.name })}
              >
                Download Sales
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadEventInvitations.mutate()}
                disabled={downloadEventInvitations.isLoading}
              >
                {downloadEventInvitations.isLoading
                  ? "Downloading..."
                  : "Download Invitations"}
              </Button>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<ReceiptIcon />}
                onClick={handleNavigateToOrders}
              >
                View Orders
              </Button>

              <ProtectedPermissionView acceptPermissions={[3]}>
                <BulkEmailResend
                  eventId={data.id}
                  eventName={data.name}
                />
              </ProtectedPermissionView>
            </Stack>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <InfoItem
                    icon={<ReceiptIcon color="primary" />}
                    label="Event ID"
                    value={data.slug + " ( " + data.id + " )"}
                  />
                  <InfoItem
                    icon={<DescriptionIcon color="primary" />}
                    label="Description"
                    value={data.description}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Gross Amount"
                    value={data.gross ? rupiah(data.gross) : "Rp 0"}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="success" />}
                    label="Total Discount"
                    value={data.total_discount ? rupiah(data.total_discount) : "Rp 0"}
                    color="success.main"
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Total Admin Fee"
                    value={data.total_fee ? rupiah(data.total_fee) : "Rp 0"}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Total Transaction Fee"
                    value={data.total_transaction_fee ? rupiah(data.total_transaction_fee) : "Rp 0"}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Total Tax"
                    value={data.total_tax ? rupiah(data.total_tax) : "Rp 0"}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Total Nett"
                    value={data.total ? rupiah(data.total) : "Rp 0"}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="Fee"
                    value={data.is_persen ? `${data.fee}%` : rupiah(data.fee)}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="SOD Fee"
                    value={data.is_persen ? `${data.sod_fee}%` : rupiah(data.sod_fee)}
                  />

                  <InfoItem
                    icon={<MonetizationOnIcon color="primary" />}
                    label="PPN"
                    value={`${data.ppn}%`}
                  />

                  <InfoItem
                    icon={<CalendarTodayIcon color="primary" />}
                    label="Created At"
                    value={moment(data.created_time).format("DD MMMM YYYY HH:mm")}
                  />

                  <InfoItem
                    icon={<PersonIcon color="primary" />}
                    label="Created By"
                    value={data.created_by}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Event Banner
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'scale(1.01)',
                        transition: 'transform 0.3s ease-in-out'
                      }
                    }}
                  >
                    <Image
                      src={process.env.REACT_APP_BACKEND_URL + data.photo}
                      alt={data.name}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 2
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Category id={event_id} event_id={data.id} />
          </Box>
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Sale Statistics</Typography>
          <BarChart
            dataset={ticketStats}
            xAxis={[{ scaleType: "band", dataKey: "grouped_days", label: "Month-Date" }]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
